import React from 'react';
import type { CatalogImportRecord, CatalogImportResult } from '@/api/models';
import {
    allFixesPlan,
    bulkFixGroupOf,
    bulkFixGroups,
    changedRowCount,
    groupPickPatches,
    makeMainPatches,
    type AllFixesPlan,
    type BulkFixCode,
    type GroupPick,
} from './catalogImportBulkFixes';
import {
    catalogImportReducer,
    createInitialCatalogImportState,
    exclusionChanges,
    type CatalogImportBranch,
    type CatalogImportFile,
    type CatalogImportState,
    type NamesDecisions,
    type ParsedLine,
    type ParsedLinePatch,
} from './catalogImportReducer';
import { undoRestoreCount } from './catalogImportSelectors';

/**
 * Which answer the store is waiting for. Each check (`previewStarted`) and
 * the import (`importStarted`) takes a new one, and so does every action
 * that makes an answer on its way obsolete: a new branch, a new file,
 * another file, a reset. An answer that comes back under an older one was
 * asked for a draft that has changed since, and the store drops it.
 */
export type CatalogImportRevision = number;

export type CatalogImportActions = {
    setBranch: (branch: CatalogImportBranch | null) => void;
    setFile: (file: CatalogImportFile | null) => void;
    setLines: (lines: ParsedLine[]) => void;
    setDecisions: (decisions: NamesDecisions) => void;
    /** Upload began reading a file (`isReadingFile`). */
    fileReadStarted: () => void;
    /** Upload's read of a file ended without reaching the store. */
    fileReadEnded: () => void;
    /** A check was sent: pass its revision back with its answer. */
    previewStarted: () => CatalogImportRevision;
    /**
     * The preview did not answer, or refused. Pass the rejection: a refusal
     * of the branch (`BRANCH_NOT_ALLOWED`) marks `scopeRefused`. Dropped,
     * returning `false`, when `revision` is no longer the latest.
     */
    previewFailed: (error: unknown, revision: CatalogImportRevision) => boolean;
    /**
     * Stores the preview, stamped with the time it arrived. Dropped,
     * returning `false`, when `revision` is no longer the latest: the check
     * was asked for another file, branch or draft.
     */
    setPreview: (
        preview: CatalogImportResult,
        revision: CatalogImportRevision,
    ) => boolean;
    excludeRows: (rowNumbers: readonly number[]) => void;
    includeRows: (rowNumbers: readonly number[]) => void;
    updateLine: (rowNumber: number, patch: ParsedLinePatch) => void;
    /** One patch to every row of a product group (the drawer's group edits). */
    updateGroup: (
        rowNumbers: readonly number[],
        patch: ParsedLinePatch,
    ) => void;
    /**
     * One kind's default (`catalogImportBulkFixes`): to every group of it, a
     * bulk action that can be undone, or with `rowNumber` to that row's
     * group only (the drawer). Returns how many rows it changed.
     */
    applyFix: (code: BulkFixCode, rowNumber?: number) => number;
    /**
     * The drawer's pick for the group of `rowNumber` (`groupPickPatches`), a
     * bulk action that can be undone. Returns how many rows it changed.
     */
    applyGroupPick: (rowNumber: number, pick: GroupPick) => number;
    /**
     * Every default at once, as one bulk action ("Import with suggested
     * fixes"). Returns the plan it applied: the lines it wrote, and the
     * rows and groups each kind changed; with no row changed at all
     * (`changedRows` empty) nothing was pushed to undo. With `against`, the
     * defaults come from that answer's problems instead of the preview's
     * (an answer to the lines as they are now); with `join`, its rows join
     * the latest "every fix" step instead of pushing another.
     */
    applyAllFixes: (options?: {
        against: CatalogImportResult;
        join: boolean;
    }) => AllFixesPlan;
    /** Exclude all, a bulk action. Returns how many rows it excluded. */
    excludeAll: (rowNumbers: readonly number[]) => number;
    /** Include all back, a bulk action. Returns how many rows it included. */
    includeAll: (rowNumbers: readonly number[]) => number;
    /**
     * Make main: the row's barcode becomes its variant's main one
     * (`primaryBarcode`). Returns how many rows it changed.
     */
    makeMainBarcode: (rowNumber: number) => number;
    /**
     * Restores the work as it was before the latest bulk action not undone
     * yet; the step re-checks. Returns how many rows it restored.
     */
    undo: () => number;
    /**
     * The import was sent: Import, Re-check and leaving wait for its answer.
     * A refusal's fresh preview is stored under the revision it returns.
     */
    importStarted: () => CatalogImportRevision;
    /**
     * The import was refused or did not answer; nothing was written. Pass
     * the rejection, as for `previewFailed`.
     */
    importFailed: (error?: unknown) => void;
    /** Stores the completed import, which also ends it. */
    setRecord: (record: CatalogImportRecord | null) => void;
    /**
     * The server refused the branch to this user on a read about it (the
     * in-shell page's history, `BRANCH_NOT_ALLOWED`): marks `scopeRefused`.
     */
    branchRefused: () => void;
    /** Import another file: drops the file, its rows and the record; keeps the branch. */
    resetImport: () => void;
    reset: () => void;
};

export type CatalogImportContextValue = {
    state: CatalogImportState;
    actions: CatalogImportActions;
};

const CatalogImportContext =
    React.createContext<CatalogImportContextValue | null>(null);

/**
 * The import flow's store, in memory only: a reload starts over, and nothing
 * is written to `localStorage` or `sessionStorage`. The wizard mounts one
 * around its steps; the in-shell import page mounts its own, keyed by its
 * branch, and seeds that branch into it.
 *
 * The store outlives the steps, so a check can answer after the step that
 * sent it was left. Each answer carries the revision its check started
 * under (`CatalogImportRevision`), and only the latest one is stored: a
 * check sent before another file, another branch or a newer check is
 * dropped, and cannot overwrite the newer preview or end the newer check.
 */
export const CatalogImportProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [state, dispatch] = React.useReducer(
        catalogImportReducer,
        undefined,
        createInitialCatalogImportState,
    );

    // Read and bumped when an action runs, never during render: an answer is
    // compared with the latest revision at the moment it arrives.
    const revision = React.useRef<CatalogImportRevision>(0);

    // The committed state, for the counts the bulk actions return: they run
    // from handlers, after the render that shows the state they act on. A
    // reducer returns nothing, so each count is the reducer's own pure helper
    // run again on that state; dispatching patches computed here instead
    // would apply them to a state that can be one dispatch newer.
    const latest = React.useRef(state);
    React.useLayoutEffect(() => {
        latest.current = state;
    }, [state]);

    // Stable for the provider's lifetime: `dispatch` never changes, so a step
    // that only writes never re-renders for the state it does not read.
    const actions = React.useMemo<CatalogImportActions>(() => {
        const nextRevision = (): CatalogImportRevision => {
            revision.current += 1;
            return revision.current;
        };
        const isLatest = (asked: CatalogImportRevision): boolean =>
            asked === revision.current;
        return {
            setBranch: (branch) => {
                nextRevision();
                dispatch({ type: 'setBranch', branch });
            },
            setFile: (file) => {
                nextRevision();
                dispatch({ type: 'setFile', file });
            },
            setLines: (lines) => dispatch({ type: 'setLines', lines }),
            setDecisions: (decisions) =>
                dispatch({ type: 'setDecisions', decisions }),
            fileReadStarted: () => dispatch({ type: 'fileReadStarted' }),
            fileReadEnded: () => dispatch({ type: 'fileReadEnded' }),
            previewStarted: () => {
                const started = nextRevision();
                dispatch({ type: 'previewStarted' });
                return started;
            },
            previewFailed: (error, asked) => {
                if (!isLatest(asked)) {
                    return false;
                }
                dispatch({ type: 'previewFailed', error });
                return true;
            },
            setPreview: (preview, asked) => {
                if (!isLatest(asked)) {
                    return false;
                }
                dispatch({
                    type: 'setPreview',
                    preview,
                    checkedAt: new Date().toISOString(),
                });
                return true;
            },
            excludeRows: (rowNumbers) =>
                dispatch({ type: 'excludeRows', rowNumbers }),
            includeRows: (rowNumbers) =>
                dispatch({ type: 'includeRows', rowNumbers }),
            updateLine: (rowNumber, patch) =>
                dispatch({ type: 'updateLine', rowNumber, patch }),
            updateGroup: (rowNumbers, patch) =>
                dispatch({ type: 'updateGroup', rowNumbers, patch }),
            applyFix: (code, rowNumber) => {
                const groups =
                    rowNumber === undefined
                        ? bulkFixGroups(latest.current, code)
                        : [bulkFixGroupOf(latest.current, code, rowNumber)];
                dispatch({ type: 'applyFix', code, rowNumber });
                return changedRowCount(
                    groups.filter((group) => group !== null),
                );
            },
            applyGroupPick: (rowNumber, pick) => {
                dispatch({ type: 'applyGroupPick', rowNumber, pick });
                return groupPickPatches(latest.current, rowNumber, pick).size;
            },
            applyAllFixes: (options) => {
                dispatch({ type: 'applyAllFixes', ...options });
                return allFixesPlan(
                    options === undefined
                        ? latest.current
                        : { ...latest.current, preview: options.against },
                );
            },
            excludeAll: (rowNumbers) => {
                dispatch({ type: 'excludeAll', rowNumbers });
                return exclusionChanges(latest.current, rowNumbers, true)
                    .length;
            },
            includeAll: (rowNumbers) => {
                dispatch({ type: 'includeAll', rowNumbers });
                return exclusionChanges(latest.current, rowNumbers, false)
                    .length;
            },
            makeMainBarcode: (rowNumber) => {
                dispatch({ type: 'makeMainBarcode', rowNumber });
                return makeMainPatches(latest.current, rowNumber).size;
            },
            undo: () => {
                dispatch({ type: 'undo' });
                return undoRestoreCount(latest.current);
            },
            importStarted: () => {
                const started = nextRevision();
                dispatch({ type: 'importStarted' });
                return started;
            },
            importFailed: (error) => dispatch({ type: 'importFailed', error }),
            setRecord: (record) => dispatch({ type: 'setRecord', record }),
            branchRefused: () => dispatch({ type: 'branchRefused' }),
            resetImport: () => {
                nextRevision();
                dispatch({ type: 'resetImport' });
            },
            reset: () => {
                nextRevision();
                dispatch({ type: 'reset' });
            },
        };
    }, []);

    const value = React.useMemo<CatalogImportContextValue>(
        () => ({ state, actions }),
        [state, actions],
    );

    return (
        <CatalogImportContext.Provider value={value}>
            {children}
        </CatalogImportContext.Provider>
    );
};

/**
 * The import flow's state and actions. Must be called inside a
 * `CatalogImportProvider`; throws otherwise, so a step mounted outside the
 * flow fails at render rather than reading an empty import.
 */
export const useCatalogImport = (): CatalogImportContextValue => {
    const value = React.useContext(CatalogImportContext);
    if (value === null) {
        throw new Error(
            'useCatalogImport must be used within a <CatalogImportProvider>',
        );
    }
    return value;
};
